import { isNikolaTeamMember, useAuth } from "@assetwatch/auth";
import type { WorkRoleCode } from "@assetwatch/work-authorization";
import { createContext, use, useEffect, useRef, useState } from "react";
import { setSimulatedWorkRole } from "../clients/workRoleOverride";
import { clearRetainedListState } from "../lib/retainedListState";
import type { CustomerId, FacilityId } from "../lib/workIds";
import { getWorkScopeSearch, type WorkSearch } from "../lib/workSearch";
import {
  useWorkContextQuery,
  type WorkCustomer,
  type WorkFacility,
} from "../queries/useWorkContextQuery";

type WorkScope = {
  customers: WorkCustomer[];
  isError: boolean;
  isNikolaTeamMember: boolean;
  isPending: boolean;
  simulatedRole: WorkRoleCode;
  /** The signed-in user's ID, when known; retained list state is keyed by it. */
  userId: string | undefined;
  selectedCustomer: WorkCustomer | null;
  selectedFacility: WorkFacility | null;
  selectCustomer: (customerId: CustomerId) => void;
  selectFacility: (facilityId: FacilityId) => void;
  setSimulatedRole: (role: WorkRoleCode) => void;
};

const WorkScopeContext = createContext<WorkScope | null>(null);

type WorkScopeProviderProps = React.PropsWithChildren<{
  canonicalizeSearch?: boolean;
  customerId?: CustomerId;
  facilityId?: FacilityId;
  onSelectionChange: (
    selection: WorkSearch,
    options?: { replace?: boolean },
  ) => void;
}>;

const DEFAULT_SIMULATED_ROLE: WorkRoleCode = "ADMINISTRATOR";

function findCustomer(
  customers: WorkCustomer[],
  requestedCustomerId: CustomerId | undefined,
) {
  return customers.find((customer) => customer.id === requestedCustomerId);
}

function findFacility(
  customer: WorkCustomer | undefined,
  requestedFacilityId: FacilityId | undefined,
) {
  return customer?.facilities.find(
    (facility) => facility.id === requestedFacilityId,
  );
}

export function WorkScopeProvider({
  canonicalizeSearch = false,
  children,
  customerId: requestedCustomerId,
  facilityId: requestedFacilityId,
  onSelectionChange,
}: WorkScopeProviderProps) {
  const { user } = useAuth();
  const userIsNikolaTeamMember = isNikolaTeamMember(user);
  const [simulatedRole, setSimulatedRole] = useState<WorkRoleCode>(
    DEFAULT_SIMULATED_ROLE,
  );
  useEffect(() => {
    setSimulatedWorkRole(userIsNikolaTeamMember ? simulatedRole : undefined);
    return () => setSimulatedWorkRole(undefined);
  }, [simulatedRole, userIsNikolaTeamMember]);
  const { data, isError, isPending } = useWorkContextQuery();
  const customers = data?.customers ?? [];
  const selectedCustomer =
    findCustomer(customers, requestedCustomerId) ?? customers[0] ?? null;
  const resolvedFacility =
    findFacility(selectedCustomer ?? undefined, requestedFacilityId) ??
    selectedCustomer?.facilities[0] ??
    null;
  const selectedFacility =
    resolvedFacility && userIsNikolaTeamMember
      ? { ...resolvedFacility, role: simulatedRole }
      : resolvedFacility;

  // Retained list state belongs to one user, customer, site and role; drop it
  // when any of them changes (a refresh keeps it).
  const userId = user?.sub || undefined;
  const scopeKey =
    selectedCustomer && selectedFacility
      ? `${userId ?? ""}:${selectedCustomer.id}:${selectedFacility.id}:${selectedFacility.role}`
      : undefined;
  const previousScopeKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!scopeKey) return;
    if (previousScopeKey.current && previousScopeKey.current !== scopeKey) {
      clearRetainedListState();
    }
    previousScopeKey.current = scopeKey;
  }, [scopeKey]);

  useEffect(() => {
    if (!selectedCustomer || !selectedFacility) {
      return;
    }

    if (
      !canonicalizeSearch &&
      requestedCustomerId === selectedCustomer.id &&
      requestedFacilityId === selectedFacility.id
    ) {
      return;
    }

    const selection = getWorkScopeSearch(selectedCustomer, selectedFacility);
    const selectionMatchesRequest =
      (selection.externalCustomerId ?? undefined) === requestedCustomerId &&
      (selection.facilityId ?? undefined) === requestedFacilityId;
    if (!canonicalizeSearch && selectionMatchesRequest) return;

    onSelectionChange(selection, { replace: true });
  }, [
    canonicalizeSearch,
    onSelectionChange,
    requestedCustomerId,
    requestedFacilityId,
    selectedCustomer,
    selectedFacility,
  ]);

  function setSelection(customerId: CustomerId, facilityId: FacilityId) {
    const customer = customers.find((item) => item.id === customerId);
    if (!customer) return;
    onSelectionChange(getWorkScopeSearch(customer, { id: facilityId }));
  }

  function selectCustomer(customerId: CustomerId) {
    const customer = customers.find((item) => item.id === customerId);
    const facility = customer?.facilities[0];
    if (customer && facility) setSelection(customer.id, facility.id);
  }

  function selectFacility(facilityId: FacilityId) {
    if (
      selectedCustomer?.facilities.some(
        (facility) => facility.id === facilityId,
      )
    ) {
      setSelection(selectedCustomer.id, facilityId);
    }
  }

  return (
    <WorkScopeContext
      value={{
        customers,
        isError,
        isNikolaTeamMember: userIsNikolaTeamMember,
        isPending,
        simulatedRole,
        userId,
        selectedCustomer,
        selectedFacility,
        selectCustomer,
        selectFacility,
        setSimulatedRole,
      }}
    >
      {children}
    </WorkScopeContext>
  );
}

export function useWorkScope(): WorkScope {
  const context = use(WorkScopeContext);
  if (!context) {
    throw new Error("useWorkScope must be used within WorkScopeProvider");
  }
  return context;
}

export function useOptionalWorkScope(): WorkScope | null {
  return use(WorkScopeContext);
}
